import * as React from "react"
import { Outlet, useLocation } from "react-router-dom"
import { Layers } from "lucide-react"

import { PageHeader } from "@/components/PageHeader"
import { SelectorDePeriodo } from "@/components/SelectorDePeriodo"
import { Toolbar, ToolbarField, ToolbarNote } from "@/components/Toolbar"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { SeccionTabs } from "@/components/SeccionTabs"
import { t } from "@/lib/i18n"

/**
 * EL GRUPO. La seccion que responde por las cuatro sociedades a la vez.
 *
 * Va al nivel de arriba, junto a Finanzas y Contabilidad, y no dentro de
 * ninguna de las dos. No es una vista mas de la contabilidad de una sociedad:
 * es otro sujeto. Meterla dentro de Contabilidad obligaria a elegir una
 * sociedad primero, que es justo lo que aqui no tiene sentido.
 *
 * Por eso tampoco usa `RequireEntity`: el consolidado cruza TODAS las
 * sociedades por definicion. El selector de sociedad de la barra superior no
 * afecta a nada de aqui dentro, y eso hay que decirlo —si no, quien cambie de
 * sociedad y vea el mismo numero va a pensar que la pantalla esta rota—.
 *
 * ---------------------------------------------------------------------------
 * YA NO HAY UN EJE «FINANZAS | CONTABILIDAD», Y HAY QUE DECIR POR QUÉ.
 *
 * Holding tuvo una pestaña «Gestión» que montaba el P&L, la caja y las cobranzas
 * de Finanzas con las cuatro sociedades sumadas, y un mando arriba para elegir
 * entre ese lado y el consolidado contable (puntos 10 y 12 de Cesar). Esa
 * pestaña era, dato por dato, la misma consulta que Finanzas sin sociedad
 * elegida —«Todas las sociedades», que es como arranca— y que el Dashboard con
 * «Todas»: el mismo endpoint, sin país, el mismo número. Tres puertas para la
 * misma suma, y la de aquí con un aviso de «esto suma, no consolida» que las
 * otras dos no tenían.
 *
 * Al quitarla, el mando se quedaba con un solo lado y se quitó también. El
 * 2026-10-04 Cesar pidió Gestión de vuelta —y con forecast—, así que volvió
 * como PESTAÑA, sin el mando de eje: Gestión | Consolidado | Análisis |
 * Intercompañía | Tesorería | Obligaciones. Ver `GestionDelGrupo`.
 */

interface FiltrosGrupo {
  periodo: string
  setPeriodo: (v: string) => void
  moneda: string
  setMoneda: (v: string) => void
  libro: string
  setLibro: (v: string) => void
}

const Ctx = React.createContext<FiltrosGrupo | null>(null)

export function useFiltrosGrupo(): FiltrosGrupo {
  const v = React.useContext(Ctx)
  if (!v) throw new Error("useFiltrosGrupo fuera de HoldingLayout")
  return v
}

/** El mes anterior al actual: el último que puede estar cerrado. */
function periodoPorDefecto(): string {
  const hoy = new Date()
  const d = new Date(Date.UTC(hoy.getUTCFullYear(), hoy.getUTCMonth() - 1, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`
}

//: Las monedas en las que un grupo latinoamericano presenta. El dolar primero
//: porque es la unica que no obliga a convertir a nadie del todo.
const MONEDAS = ["USD", "PEN", "MXN", "BRL", "EUR"]

/*
  LOS TRES LIBROS, CON EL MISMO NOMBRE QUE EN EL RESTO DEL PRODUCTO.

  Aquí el primero se llamaba «Contable» y en Contabilidad «Statutory», así que
  eran dos nombres para el mismo libro en dos pantallas. Al renombrarlos por su
  destinatario —ver `BOOK_LABELS` en `lib/types.ts`, que contesta el «¿qué es
  statutory (PE)?» de Cesar— este sitio se quedó atrás y la incoherencia pasó a
  ser visible: «Contable» además no distingue nada, porque los tres lo son.
*/
const LIBROS = [
  { id: "statutory", label: "Societario" },
  { id: "tax", label: "Tributario" },
  { id: "management", label: "Gerencial" },
]

/** ¿Estamos en Gestión, que trae su propia barra de año? */
function useEsGestion() {
  const { pathname } = useLocation()
  return pathname.includes("/holding/gestion")
}

function Tabs() {
  const tabs = [
    /*
      GESTIÓN PRIMERO. Cesar (punto 12 y de nuevo el 2026-10-04): Holding entra
      por Finanzas. Es el P&L, la caja, el forecast y las cobranzas del grupo,
      sumados en USD; el resto es el consolidado contable y sus insumos.
    */
    { to: "gestion", label: "Gestión" },
    { to: "consolidado", label: "Consolidado" },
    // ANALISIS va junto a Consolidado y no al final: son las dos caras de la
    // misma pregunta —la foto y la pelicula— y separarlas hace que la segunda
    // parezca un anexo. El resto de pestañas son insumos del consolidado.
    { to: "analisis", label: "Análisis" },
    { to: "intercompania", label: "Intercompañía" },
    { to: "tesoreria", label: "Tesorería" },
    { to: "obligaciones", label: "Obligaciones" },
    // «Estructura» vive al pie de Consolidado: son cuatro filas que se declaran una vez.
  ]
  return (
    <SeccionTabs tabs={tabs} ariaLabel="Vistas del grupo" />
  )
}

function BarraDeFiltros() {
  const f = useFiltrosGrupo()
  return (
    <Toolbar>
      <ToolbarField label={t("Período")} htmlFor="grupo-periodo">
        {/*
          NO es un `<input type="month">`: ese se pinta con el idioma del
          NAVEGADOR y escribía «August 2026» sobre una pantalla en español.
          Ver `SelectorDePeriodo`.
        */}
        <SelectorDePeriodo
          id="grupo-periodo"
          value={f.periodo}
          onChange={f.setPeriodo}
        />
      </ToolbarField>

      <ToolbarField label={t("Moneda de presentación")} htmlFor="grupo-moneda">
        <select
          id="grupo-moneda"
          value={f.moneda}
          onChange={(e) => f.setMoneda(e.target.value)}
          className={cn(
            "code h-7 rounded-md border bg-surface px-2 text-xs",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          {MONEDAS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </ToolbarField>

      <div className="space-y-1">
        <Label htmlFor="grupo-libro">{t("Libro")}</Label>
        <select
          id="grupo-libro"
          value={f.libro}
          onChange={(e) => f.setLibro(e.target.value)}
          className={cn(
            "h-7 rounded-md border bg-surface px-2 text-xs",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          {LIBROS.map((l) => (
            <option key={l.id} value={l.id}>
              {l.label}
            </option>
          ))}
        </select>
      </div>

      <ToolbarNote icon={Layers} className="ml-auto">
        {t("El selector de sociedad de arriba no afecta a esta sección: el consolidado cruza las cuatro por definición.")}</ToolbarNote>
    </Toolbar>
  )
}

export function HoldingLayout() {
  const [periodo, setPeriodo] = React.useState(periodoPorDefecto)
  const [moneda, setMoneda] = React.useState("USD")
  const [libro, setLibro] = React.useState("statutory")

  const valor = React.useMemo<FiltrosGrupo>(
    () => ({ periodo, setPeriodo, moneda, setMoneda, libro, setLibro }),
    [periodo, moneda, libro],
  )

  const esGestion = useEsGestion()

  return (
    <Ctx.Provider value={valor}>
      <div className="space-y-3">
        <PageHeader
          eyebrow="El grupo"
          title={t("Holding")}
          description={t("Las cuatro sociedades como una sola: en Gestión, sumadas en USD; en Consolidado, convertidas según la NIC 21, con lo que se facturan entre ellas eliminado y la participación no controladora separada.")}
        />
        <Tabs />
        {/* Gestión mira el modelo por AÑO y trae su propia barra; ésta es la del consolidado (mes, moneda, libro). */}
        {esGestion ? null : <BarraDeFiltros />}
        <Outlet />
      </div>
    </Ctx.Provider>
  )
}
